import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CheckoutHeader from '../components/checkout/CheckoutHeader';
import ProgressStepper from '../components/checkout/ProgressStepper';
import OrderSummary from '../components/checkout/OrderSummary';
import PassengerForm from '../components/checkout/PassengerForm';
import PaymentSection from '../components/checkout/PaymentSection';
import PolicyAcceptance from '../components/checkout/PolicyAcceptance';
import TrustMicrocopy from '../components/checkout/TrustMicrocopy';
import MobileOrderSummary from '../components/checkout/MobileOrderSummary';
import { Button } from '../components/ui/button';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('paypal');
  const [bookingRef] = useState(() => `VPG-${Date.now().toString().slice(-8)}`);
  
  const [passengerDetails, setPassengerDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    flightNumber: '',
    specialRequests: '',
    termsAccepted: false,
  });

  const [errors, setErrors] = useState({});

  // Get booking details from URL params or use defaults
  const bookingDetails = {
    pickup: searchParams.get('pickup') || 'Istanbul Airport (IST)',
    dropoff: searchParams.get('dropoff') || 'Taksim Square',
    date: searchParams.get('date') || '2026-01-15',
    time: searchParams.get('time') || '14:00',
    passengers: parseInt(searchParams.get('passengers')) || 2,
    luggage: parseInt(searchParams.get('luggage')) || parseInt(searchParams.get('passengers')) || 2,
    vehicleName: searchParams.get('vehicle') || 'Business Sedan',
    vehicleType: searchParams.get('vehicleType') || 'VAN',
    vehicleDesc: 'Mercedes E-Class or similar',
    price: searchParams.get('price') || '65',
    currency: '€',
    maxPassengers: parseInt(searchParams.get('maxPassengers')) || 8,
    maxLuggage: parseInt(searchParams.get('maxLuggage')) || 6,
  };

  // Capacity validation guard
  const [capacityError, setCapacityError] = useState(null);

  useEffect(() => {
    // Check if capacity is exceeded
    if (bookingDetails.passengers > bookingDetails.maxPassengers) {
      setCapacityError(`Passenger count (${bookingDetails.passengers}) exceeds vehicle capacity (${bookingDetails.maxPassengers})`);
    } else if (bookingDetails.luggage > bookingDetails.maxLuggage) {
      setCapacityError(`Luggage count (${bookingDetails.luggage}) exceeds vehicle capacity (${bookingDetails.maxLuggage})`);
    } else {
      setCapacityError(null);
    }
  }, [bookingDetails.passengers, bookingDetails.luggage, bookingDetails.maxPassengers, bookingDetails.maxLuggage]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!passengerDetails.firstName.trim()) {
      newErrors.firstName = 'Please enter your first name';
    }
    if (!passengerDetails.lastName.trim()) {
      newErrors.lastName = 'Please enter your last name';
    }
    if (!passengerDetails.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(passengerDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!passengerDetails.phone.trim()) {
      newErrors.phone = 'Please enter your phone number';
    } else if (!/^[+]?[0-9\s-]{8,}$/.test(passengerDetails.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!passengerDetails.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? value : value;
    setPassengerDetails(prev => ({ ...prev, [name]: newValue }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContinueToPayment = () => {
    if (validateForm()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompleteBooking = async () => {
    // Capacity guard - prevent booking if exceeded
    if (capacityError) {
      toast.error('Please adjust your selection. Vehicle capacity exceeded.', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return;
    }

    if (!policyAccepted) {
      toast.error('Please accept the terms and conditions', {
        style: { background: 'hsl(24 100% 50%)', color: 'white', border: 'none' }
      });
      return;
    }

    setIsProcessing(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setCurrentStep(3);
    setIsProcessing(false);
    
    toast.success('Booking confirmed! Check your email for details.');
  };

  const isFormValid = passengerDetails.firstName && passengerDetails.lastName && 
                      passengerDetails.email && passengerDetails.phone;

  return (
    <div className="min-h-screen bg-card">
      <CheckoutHeader />
      
      <main className="pt-20 pb-32 lg:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Stepper */}
          <ProgressStepper currentStep={currentStep} />

          {/* Mobile Order Summary */}
          <div className="lg:hidden mb-6">
            <MobileOrderSummary bookingDetails={bookingDetails} />
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form Column */}
            <div className="lg:col-span-3 space-y-6">
              {/* Capacity Error Alert */}
              {capacityError && (
                <div className="bg-primary/10 border border-primary rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-foreground font-medium text-sm">{capacityError}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Please go back and select a different vehicle or adjust your requirements.
                    </p>
                    <Button
                      variant="cta"
                      size="sm"
                      className="mt-3"
                      onClick={() => navigate(-1)}
                    >
                      Go Back to Vehicle Selection
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <>
                  {/* Passenger Details */}
                  <PassengerForm
                    passengerDetails={passengerDetails}
                    errors={errors}
                    onChange={handleInputChange}
                  />

                  {/* Continue Button */}
                  <Button
                    variant="ctaLarge"
                    size="xl"
                    className="w-full"
                    onClick={handleContinueToPayment}
                    disabled={!isFormValid}
                  >
                    Continue to Payment
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </>
              )}

              {currentStep === 2 && (
                <>
                  {/* Payment Section */}
                  <PaymentSection
                    selectedPayment={selectedPayment}
                    onSelectPayment={setSelectedPayment}
                  />

                  {/* Policy Acceptance */}
                  <PolicyAcceptance
                    accepted={policyAccepted}
                    onAcceptChange={setPolicyAccepted}
                  />

                  {/* Trust Microcopy */}
                  <TrustMicrocopy />

                  {/* Complete Booking Button */}
                  <Button
                    variant="ctaLarge"
                    size="xl"
                    className="w-full"
                    onClick={handleCompleteBooking}
                    disabled={!policyAccepted || isProcessing}
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Pay Securely
                        <ArrowRight className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </>
              )}

              {currentStep === 3 && (
                <div className="bg-secondary rounded-xl p-8 text-center border border-border">
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="font-display text-2xl font-bold text-foreground mb-2">
                    Booking Confirmed!
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Your transfer has been booked. A confirmation email has been sent to {passengerDetails.email}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Booking Reference: <span className="font-mono font-semibold text-foreground">{bookingRef}</span>
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="border-border text-foreground hover:bg-accent"
                  >
                    Back to Home
                  </Button>
                </div>
              )}
            </div>

            {/* Order Summary - Desktop */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="sticky top-24">
                <OrderSummary bookingDetails={bookingDetails} />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Sticky CTA */}
      {currentStep < 3 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t-2 border-primary p-4 lg:hidden z-50">
          {currentStep === 1 ? (
            <Button
              variant="ctaLarge"
              size="xl"
              className="w-full"
              onClick={handleContinueToPayment}
              disabled={!isFormValid}
            >
              Continue to Payment
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          ) : (
            <Button
              variant="ctaLarge"
              size="xl"
              className="w-full"
              onClick={handleCompleteBooking}
              disabled={!policyAccepted || isProcessing}
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </span>
              ) : (
                `Pay ${bookingDetails.currency}${bookingDetails.price}`
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
